import { useQueryClient } from '@tanstack/react-query';
import { type FormEvent, useState } from 'react';
import { useNavigate } from 'react-router';

import { errorMessage } from '@/shared/api/errors';
import { Button } from '@/shared/ui/Button';
import { Callout } from '@/shared/ui/Callout';
import { Card } from '@/shared/ui/Card';
import { PasswordInput } from '@/shared/ui/PasswordInput';
import { PageHeader } from '@/shared/ui/PageHeader';

import { sessionQueryKey } from './api';
import { MIN_PASSWORD, PASSWORD_HINT } from '@/shared/session/password';
import { useChangePassword, useSession } from './useSession';

/**
 * One's own account: change the password. It is also where a person lands when an
 * administrator typed their password for them (`must_change_password`): the API refuses
 * everything else until they choose their own, and this page says so.
 *
 * One card, the fields stacked: there is one thing to do here.
 */
export function ProfilePage() {
  const session = useSession();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const change = useChangePassword();
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [repeat, setRepeat] = useState('');
  const mustChange = session.data?.must_change_password === true;
  const mismatch = repeat !== '' && next !== repeat;

  const platform = session.data?.is_superadmin === true;
  const who = [
    session.data?.email,
    platform ? 'Superadministrador' : session.data?.is_admin ? 'Administrador' : null,
  ]
    .filter(Boolean)
    .join(' · ');
  // Contract v10.12: the mailboxes this person reads, which is what the inbox shows.
  const mailboxes = session.data?.mailboxes ?? [];

  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (mismatch) return;
    change.mutate(
      { current_password: current, new_password: next },
      {
        onSuccess: (user) => {
          queryClient.setQueryData(sessionQueryKey, user);
          setCurrent('');
          setNext('');
          setRepeat('');
          if (mustChange) void navigate('/correos', { replace: true });
        },
      },
    );
  }

  return (
    <>
      <PageHeader
        title={session.data?.full_name ?? session.data?.email ?? 'Mi cuenta'}
        description={who || undefined}
      />

      {session.data ? (
        <Card title="Tus buzones" description="Lo que ves en la bandeja es lo que entra en estos.">
          {platform ? (
            <p className="text-ink-2 text-[14px]">
              Todos: un superadministrador ve todos los buzones.
            </p>
          ) : mailboxes.length ? (
            <ul className="text-ink flex flex-wrap gap-2 text-[14px]">
              {mailboxes.map((each) => (
                <li key={each} className="bg-paper-2 rounded-md px-2 py-1 font-mono text-[13px]">
                  {each}
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-ink-2 text-[14px]">
              Ninguno todavía. Pídele a quien administra la aplicación que te asigne el buzón que
              lees.
            </p>
          )}
        </Card>
      ) : null}

      <Card
        title="Cambiar la contraseña"
        description="Cierra las demás sesiones abiertas con tu cuenta; esta se queda."
        className="max-w-lg"
      >
        {mustChange ? (
          <Callout tone="warn" title="Elige tu contraseña antes de seguir" role="alert">
            La contraseña con la que has entrado la puso un administrador y la ha visto más gente
            que tú. Hasta que elijas la tuya, el resto de la aplicación no responde.
          </Callout>
        ) : null}

        <form onSubmit={submit} className="flex flex-col gap-3">
          <PasswordInput
            label="Contraseña actual"
            autoComplete="current-password"
            value={current}
            onChange={(event) => setCurrent(event.target.value)}
            required
          />
          <PasswordInput
            label="Contraseña nueva"
            autoComplete="new-password"
            value={next}
            onChange={(event) => setNext(event.target.value)}
            minLength={MIN_PASSWORD}
            hint={PASSWORD_HINT}
            required
          />
          <PasswordInput
            label="Repite la nueva"
            autoComplete="new-password"
            value={repeat}
            onChange={(event) => setRepeat(event.target.value)}
            error={mismatch ? 'No coinciden.' : undefined}
            required
          />
          <div className="flex items-center gap-3">
            <Button
              type="submit"
              disabled={change.isPending || next.length < MIN_PASSWORD || mismatch || !current}
            >
              {change.isPending ? 'Cambiando…' : 'Cambiar la contraseña'}
            </Button>
            <p role="status" className="text-ok text-[13.5px]">
              {change.isSuccess ? 'Cambiada.' : ''}
            </p>
          </div>
          {change.isError ? (
            <p role="alert" className="text-danger text-[13.5px]">
              {errorMessage(change.error)}
            </p>
          ) : null}
        </form>
      </Card>
    </>
  );
}
